import { queryOptions, useQuery } from '@tanstack/react-query'

import { marketEndpoints } from '@/api/endpointCache'
import { marketStateIsOpen } from '@/features/market/api/marketMappers'

const STATUS_POLL_OPEN_MS = 60_000

/**
 * Closed-market ceiling: how rarely this checks when the next open is not
 * imminent (or not known at all). Never fully off — something has to notice
 * NEPSE reopened — but there is no reason to check every minute at 2am when
 * nothing is going to change for hours.
 */
const STATUS_POLL_CLOSED_MS = 5 * 60_000

/**
 * Added on top of the time remaining until `next_open` so the tick lands
 * just AFTER the backend has flipped `state`, not a hair before it — a poll
 * that fires one second early just re-confirms "still closed" and waits out
 * another full interval for nothing.
 */
const STATUS_POLL_NEAR_OPEN_BUFFER_MS = 2_000

/** Floor once the countdown is tight, so a clock a few seconds off from the
 * server (or a `next_open` already in the past) cannot turn this into a
 * busy-poll. */
const STATUS_POLL_MIN_MS = 5_000

/**
 * How long to wait before checking again while the market is closed.
 *
 * `MarketStatus.next_open` is the backend's own answer for when NEPSE
 * resumes — it already accounts for weekends and holidays, so using it
 * directly (rather than re-deriving "is tomorrow a trading day" here) is not
 * duplicating that calendar, just reading what it already computed. That
 * sidesteps the drift risk a locally-guessed calendar would have.
 *
 * Far from the open, this returns the same `STATUS_POLL_CLOSED_MS` ceiling
 * as before — a tab left open overnight still does not hammer `/market/status`
 * for hours. As the known open time approaches, the interval tightens to
 * match, landing on (or just after) `next_open` instead of waiting out
 * whatever fraction of the five-minute cadence was left. That closes the
 * up-to-five-minutes-blind window the flat cadence had at the open: the
 * next tick after `next_open` passes is what actually detects it, and this
 * makes sure that tick is close to `next_open`, not up to 5 minutes later.
 *
 * Falls back to the ceiling when `next_open` is absent or unparseable —
 * the rare case where even the backend does not have a next session to
 * offer — rather than guessing at one.
 */
export const closedPollIntervalMs = (nextOpen: string | null, now: number): number => {
  if (nextOpen === null) return STATUS_POLL_CLOSED_MS
  const msUntilOpen = new Date(nextOpen).getTime() - now
  if (Number.isNaN(msUntilOpen)) return STATUS_POLL_CLOSED_MS
  return Math.min(
    STATUS_POLL_CLOSED_MS,
    Math.max(STATUS_POLL_MIN_MS, msUntilOpen + STATUS_POLL_NEAR_OPEN_BUFFER_MS),
  )
}

/**
 * Deliberately its own top-level key, not nested under `marketKeys.all`
 * (`['market']` in features/market/api/keys.ts). TanStack Query invalidates
 * by prefix, so anything shaped `['market', ...]` is swept by
 * `invalidateQueries({ queryKey: marketKeys.all })` — the index card's
 * refresh button today, and any future "reload everything market-related"
 * call. Market status is infrastructure every feature's polling gate reads;
 * it must not be invalidated by accident just because its name starts with
 * the same word as the feature that also happens to poll.
 */
const marketStatusKeys = {
  status: () => ['market-status'] as const,
}

export interface MarketStatusResult {
  /** True once NEPSE is trading, by the backend's own clock. False both while
   *  the first response is still in flight and once it has resolved closed —
   *  unchanged from before `isPending` existed, so every caller that only
   *  ever read `isOpen` keeps working exactly as it did. */
  isOpen: boolean
  /**
   * True until the first `/market/status` response lands. Distinct from
   * `isOpen === false`: a caller that fires a request gated on `isOpen` alone
   * would send it — and get a real 409 — the instant the status is merely
   * unknown, not actually closed. Gate on `!isPending && isOpen` instead.
   */
  isPending: boolean
  /** `next_open` off the wire, unparsed — null when the backend has no next
   *  session to report (also the case while `isPending`). */
  nextOpen: string | null
  /** `last_close` off the wire, unparsed — null likewise. */
  lastClose: string | null
}

/**
 * Exported for the `_app` layout's loader, which starts this read with the
 * rail's (see `prefetchMarketRail`) so every live query's polling gate is
 * known by the time the first screen mounts, rather than one round trip
 * after it.
 */
export const marketStatusQueryOptions = () =>
  queryOptions({
    queryKey: marketStatusKeys.status(),
    // `({ client }) => ...` rather than a bare `marketApi.status` reference:
    // this reads through the endpoint cache so this hook's poll shares its
    // `/market/status` entry with any service that also fetches it, and pulls
    // the QueryClient off the query's own context rather than the app
    // singleton — see endpointCache.ts's comment on why services take an
    // optional `client` — so a test's own QueryClientProvider client is what
    // gets used, with no leakage between tests.
    queryFn: ({ client }) => marketEndpoints(client).status(),
    staleTime: STATUS_POLL_OPEN_MS / 2,
    // A function, not a fixed number, so the interval itself can react to
    // the state it is polling for: fast while the market trades, slow while
    // it is shut, and back to fast the moment a slow tick reports OPEN.
    refetchInterval: (query) => {
      const data = query.state.data
      if (data === undefined) return STATUS_POLL_CLOSED_MS
      return marketStateIsOpen(data.state)
        ? STATUS_POLL_OPEN_MS
        : closedPollIntervalMs(data.next_open, Date.now())
    },
  })

export const useMarketStatus = (): MarketStatusResult => {
  const query = useQuery(marketStatusQueryOptions())

  const data = query.data
  return {
    isOpen: data === undefined ? false : marketStateIsOpen(data.state),
    isPending: data === undefined,
    nextOpen: data?.next_open ?? null,
    lastClose: data?.last_close ?? null,
  }
}
